import { IconGame } from "@assets";
import BackButton from "@components/inputs/BackButton";
import { envConfig } from "@configs/env";
import { GAME_SOURCE, useStatsigTracking } from "@configs/statsig";
import { APP_INSTALLER_HEIGHT } from "@constants";
import useTypedSelector from "@hooks/useTypedSelector";
import useUserMode from "@hooks/useUserMode";
import { WithAccountId } from "@types";
import { useDebounce } from "@uidotdev/usehooks";
import {
  EmptyState,
  GameCategoryModel,
  GameModel,
  GameStatus,
  GetGameListRequest,
  PageTitle,
  TextField,
  cn,
  useCustomEventsListener,
  useInfiniteQuery,
} from "kz-ui-sdk";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBottomScrollListener } from "react-bottom-scroll-listener";
import { useTranslation } from "react-i18next";
import { MdSearch } from "react-icons/md";
import { GameEntry, GameEntrySkeleton } from "..";
import { CATEGORY_POPULAR_SLUG } from "../../constants";
import { useGameDataSource, useGameGateway } from "../../hooks";
import { EVENT_HIDE_GAME_SEARCH_MODAL, EVENT_SHOW_GAME_SEARCH_MODAL } from "./constants";

interface SearchModalProps extends PropsWithChildren {
  defaultOpen?: boolean;
  onClose?: () => void;
  preloadCategory?: GameCategoryModel;
}

const SEARCH_MODAL_ANIMATION_DURATION = 300;
const ITEMS_PER_PAGE = 10;
const initialGameQuery: GetGameListRequest["query"] = {
  limit: ITEMS_PER_PAGE,
  offset: 0,
  status: GameStatus.Active,
};

const GameSearchModal = ({ defaultOpen = false, onClose, preloadCategory }: SearchModalProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const { t } = useTranslation();
  const isDisplayAppInstaller = useTypedSelector((state) => state.preferences.isDisplayAppInstaller);
  const { isGuest } = useUserMode();
  const refSearchInput = useRef<HTMLInputElement>(null);

  const resetSearchTerm = () => {
    setTimeout(() => {
      setSearchTerm(undefined);
      if (refSearchInput.current) {
        refSearchInput.current.value = "";
      }
    }, SEARCH_MODAL_ANIMATION_DURATION);
  };

  // Handle hide game search modal event
  useCustomEventsListener({
    [EVENT_HIDE_GAME_SEARCH_MODAL]: () => {
      setIsOpen(false);
      resetSearchTerm();
    },
    [EVENT_SHOW_GAME_SEARCH_MODAL]: () => {
      setIsOpen(true);
    },
  });

  const popularCategoryId = useMemo(() => {
    if (preloadCategory?.slug === CATEGORY_POPULAR_SLUG) {
      return preloadCategory.id;
    }
  }, [preloadCategory?.id, preloadCategory?.slug]);

  const [gameQueryParams, setQueryParams] = useState<GetGameListRequest["query"]>({
    ...initialGameQuery,
    // Default to popular category
    category: popularCategoryId,
  });
  const refSearchResultContainer = useBottomScrollListener<HTMLDivElement>(
    () => {
      if (hasNextPage && !isFetchingMoreResults) {
        fetchNextPage();
      }
    },
    {
      offset: 40,
    },
  );

  const { open: openGame, providerMap } = useGameGateway();
  const { getGameList } = useGameDataSource();

  const [searchTerm, setSearchTerm] = useState<string>();
  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  const searchTermRef = useRef<string | null>(null);

  // Track search when term changes
  const { trackGameSearch, trackGameSearchSelect } = useStatsigTracking();

  // Fetch game list
  const {
    data: gameData,
    isFetchingInitialPage: isSearching,
    isFetchingNextPage: isFetchingMoreResults,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<WithAccountId<GetGameListRequest>, GameModel>(
    getGameList,
    {
      query: gameQueryParams,
      accountId: envConfig.accountId,
    },
    {
      onSuccess: (data) => {
        if (data?.meta?.offset === 0 && searchTermRef.current !== null)
          trackGameSearch(debouncedSearchTerm, { resultCount: data?.meta?.count });
      },
    },
  );

  // Update game list when search term changes
  useEffect(() => {
    // Load popular category games if search term is empty
    const categoryId = !!debouncedSearchTerm ? undefined : popularCategoryId;

    setQueryParams({
      ...initialGameQuery,
      search: debouncedSearchTerm,
      category: categoryId,
    });
  }, [debouncedSearchTerm, popularCategoryId]);

  // Scroll to top when search term changes
  useEffect(() => {
    if (refSearchResultContainer.current) {
      refSearchResultContainer.current.scrollTo(0, 0);
    }
  }, [debouncedSearchTerm, refSearchResultContainer]);

  const handleOnBack = () => {
    setIsOpen(false);
    resetSearchTerm();
    onClose?.();
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value?.trim() ?? undefined);
  };

  useEffect(() => {
    searchTermRef.current = searchTerm ?? null;
  }, [searchTerm]);

  const handleOpenGameFromSearch = useCallback(
    (g: GameModel) => {
      openGame(g, { source: GAME_SOURCE.SEARCH, searchTerm: debouncedSearchTerm });
    },
    [openGame, debouncedSearchTerm],
  );

  const handleSelectGameWrapper = useCallback(
    (g: GameModel) => {
      trackGameSearchSelect(g, { source: GAME_SOURCE.SEARCH, searchTerm: debouncedSearchTerm });
    },
    [debouncedSearchTerm, trackGameSearchSelect],
  );

  return (
    <>
      {
        <div
          className={cn(
            "fixed start-1/2 top-0 z-[15] flex h-dvh w-screen max-w-md flex-col bg-black/70 px-5 pb-0 pt-3 opacity-100 backdrop-blur-2xl transition-opacity -translate-start-1/2",
            {
              "pointer-events-none !opacity-0": !isOpen,
            },
          )}
          style={{
            height: `calc(100vh - ${isDisplayAppInstaller ? APP_INSTALLER_HEIGHT : "0"}px)`,
            top: isDisplayAppInstaller ? APP_INSTALLER_HEIGHT : 0,
          }}
        >
          <PageTitle
            label={t("Search")}
            backButton={
              <BackButton
                onClick={handleOnBack}
                aria-label="Game search back"
                data-element-id="button-game-search-back"
              />
            }
            className="mb-3.5"
          />
          <TextField
            placeholder={t("Find a game")}
            icon={<MdSearch size={24} />}
            classes={{
              "input&": "font-semibold w-full !ps-10",
              "iconContainer&": "!top-3.5 !start-3",
            }}
            size={"lg"}
            onChange={handleSearchInputChange}
            ref={refSearchInput}
            name="input-game-search"
            data-element-id="input-game-search"
            aria-label="Game search input"
          />

          <div
            className="scrollbar-primary -mx-6 mt-5 grow overflow-y-auto px-6"
            ref={refSearchResultContainer}
          >
            <div className="flex flex-col gap-y-4 pb-4">
              {!isSearching &&
                gameData.map((game) => (
                  <GameEntry
                    key={[game.providerRef, game.gameProviderId].join("-")}
                    variant={"row"}
                    game={game}
                    onOpen={handleOpenGameFromSearch}
                    providerName={providerMap[game.gameProviderId]?.name}
                    sourceRef={providerMap[game.gameProviderId]?.sourceRef}
                    maintenance={(game.isMaintenance || providerMap[game.gameProviderId]?.isMaintenance) && !isGuest}
                    onClickGameWrapper={handleSelectGameWrapper}
                    aria-label="Game search entry"
                  />
                ))}
              {(isSearching || isFetchingMoreResults) && (
                <GameEntrySkeleton
                  variant="row"
                  count={ITEMS_PER_PAGE}
                />
              )}
              {!isSearching && gameData.length === 0 && (
                <EmptyState
                  title={t("No games found")}
                  subTitle={t("Try a different search term")}
                  icon={<IconGame className="text-content-soft" />}
                />
              )}
            </div>
          </div>
        </div>
      }
    </>
  );
};

export default GameSearchModal;
